// src/config/aboutConfig.ts
// Content source of truth for the About page.
// Sections are added here as they are implemented — profile, snapshot,
// domains, approach, capabilities, timeline, cta.

export interface AboutFocusArea {
  label: string;
  detail: string;
}

export interface AboutCtaAction {
  label: string;
  to: string;
}

export type AboutDomainIcon = 'aviation' | 'iot' | 'asset' | 'energy';

export type AboutDomainDiagram = 'flightPath' | 'mesh' | 'hierarchy' | 'pipeline';

export interface AboutDomain {
  id: string;
  index: string;
  industry: string;
  shortLabel: string;
  icon: AboutDomainIcon;
  diagram: AboutDomainDiagram;
  context: string;
  scope: string;
  contribution: string;
  concepts: string[];
}

export interface AboutProfile {
  eyebrow: string;
  role: string;
  supporting: string;
  experience: {
    value: string;
    label: string;
  };
  principle: string;
  focusAreas: AboutFocusArea[];
  cta: {
    primary: AboutCtaAction;
    secondary: AboutCtaAction;
  };
}

export interface AboutDomainsSection {
  eyebrow: string;
  heading: string;
  lede: string;
  stackNote: string;
  items: AboutDomain[];
}

export interface AboutApproachStage {
  id: string;
  index: string;
  title: string;
  short: string;
  intent: string;
  detail: string;
  patterns: string[];
  evidence: string;
}

export interface AboutApproachSection {
  eyebrow: string;
  heading: string;
  lede: string;
  note: string;
  stages: AboutApproachStage[];
}

export type AboutCapabilityIcon =
  | 'backend'
  | 'architecture'
  | 'data'
  | 'messaging'
  | 'frontend'
  | 'cloud';

export interface AboutCapabilityItem {
  label: string;
  /**
   * `true` when the technology is evidenced by the professional
   * experience described earlier on this page. `false` means it is
   * listed for coverage — not as a claim about depth.
   */
  documented: boolean;
}

export interface AboutCapabilityGroup {
  id: string;
  index: string;
  title: string;
  role: string;
  icon: AboutCapabilityIcon;
  items: AboutCapabilityItem[];
}

export interface AboutCapabilitiesSection {
  eyebrow: string;
  heading: string;
  lede: string;
  additionalLabel: string;
  note: string;
  groups: AboutCapabilityGroup[];
}

export interface AboutTimelineEngagement {
  id: string;
  ref: string;
  domain: string;
  client: string;
  purpose: string;
  contribution: string;
  responsibilities: string[];
  technologies: string[];
  detail: string[];
}

export interface AboutTimelineSection {
  eyebrow: string;
  heading: string;
  lede: string;
  note: string;
  position: {
    company: string;
    role: string;
    period: string;
    duration: string;
  };
  items: AboutTimelineEngagement[];
}

export interface AboutClientCtaItem {
  label: string;
  detail: string;
}

export interface AboutClientCtaSection {
  eyebrow: string;
  heading: string;
  lede: string;
  capabilities: AboutClientCtaItem[];
  cta: {
    primary: AboutCtaAction;
    secondary: AboutCtaAction;
  };
}

export const aboutConfig: {
  profile: AboutProfile;
  domains: AboutDomainsSection;
  approach: AboutApproachSection;
  capabilities: AboutCapabilitiesSection;
  timeline: AboutTimelineSection;
  clientCta: AboutClientCtaSection;
} = {
  profile: {
    eyebrow: 'Enterprise Backend Engineering',
    role: 'Backend Engineer — Enterprise Application Development',
    supporting:
      'I design and build backend services and REST APIs across the C# and .NET ecosystem. The work sits inside real enterprise systems — operations, assets, connected devices and field data — where maintainability and reliability are non-negotiable.',
    experience: {
      value: '4.5+ years',
      label:
        'Professional software development across aviation operations, smart IoT, IT/OT asset management and ISMS, and energy and resources systems.',
    },
    principle: 'Building software systems that solve real business problems.',
    focusAreas: [
      { label: 'Backend & APIs', detail: 'REST services, domain models, C# / .NET' },
      { label: 'Architecture', detail: 'Clean Architecture, CQRS, Repository, SOLID' },
      {
        label: 'Integrations & Systems',
        detail: 'Azure Service Bus, event-driven workflows, background jobs',
      },
    ],
    cta: {
      primary: { label: "Let's Work Together", to: '/get-in-touch' },
      secondary: { label: 'View Projects', to: '/projects' },
    },
  },

  domains: {
    eyebrow: 'Engineering domains',
    heading: 'Systems built across four business domains',
    lede: 'The engineering problems changed with every domain — operational scheduling, device data, compliance evidence, field collection. The way I approach them did not.',
    stackNote:
      'C# / .NET / .NET Core · ASP.NET Core REST APIs · Azure Service Bus · Hangfire background jobs · PostgreSQL / SQL Server / Oracle / MongoDB · React (TypeScript) for frontend integration',
    items: [
      {
        id: 'aviation',
        index: '01',
        industry: 'Aviation & Operations',
        shortLabel: 'Aviation',
        icon: 'aviation',
        diagram: 'flightPath',
        context:
          'Operators coordinating trips and schedules, where day-to-day planning depends on operational data being accurate and available at the right time.',
        scope:
          'Trip and operations management — planning, tracking and reporting across trips and the operational activity around them.',
        contribution:
          'Backend services and REST APIs built on Clean Architecture with CQRS and the Repository pattern, so trip and scheduling rules stay independent of persistence and transport.',
        concepts: [
          'Clean Architecture',
          'CQRS',
          'Repository Pattern',
          'OOP / SOLID',
          'REST API design',
        ],
      },
      {
        id: 'iot',
        index: '02',
        industry: 'Smart IoT & Connected Devices',
        shortLabel: 'Smart IoT',
        icon: 'iot',
        diagram: 'mesh',
        context:
          'A connected home and appliance platform where device state has to move reliably between devices, the platform, and the people using it.',
        scope:
          'Smart home and appliance management — device onboarding, device state, and the asynchronous processing that keeps device data consistent.',
        contribution:
          'Event-driven services connecting device-facing workflows to application services through Azure Service Bus, with Hangfire carrying scheduled and background processing off the request path.',
        concepts: [
          'Event-Driven Architecture',
          'Azure Service Bus',
          'Background & Scheduled Jobs',
          'Outbox Messaging',
          'Clean Architecture',
        ],
      },
      {
        id: 'asset',
        index: '03',
        industry: 'IT/OT Asset Management & ISMS',
        shortLabel: 'IT/OT & ISMS',
        icon: 'asset',
        diagram: 'hierarchy',
        context:
          'Organisations tracking IT and OT assets while running an information security management system, where asset records and compliance evidence have to stay consistent with each other.',
        scope:
          'Asset lifecycle and ISMS workflows — asset records, compliance tracking, and the reporting that supports audit.',
        contribution:
          'Domain and persistence concerns separated behind a repository abstraction, exposing REST APIs consumed by React front-ends, with the implementation approach agreed alongside frontend developers rather than handed over.',
        concepts: [
          'Repository Pattern',
          'Clean Architecture',
          'OOP / SOLID',
          'REST API design',
          'TDD',
        ],
      },
      {
        id: 'energy',
        index: '04',
        industry: 'Energy & Resources',
        shortLabel: 'Energy & Resources',
        icon: 'energy',
        diagram: 'pipeline',
        context:
          'Energy and resources operations where field-originated data has to be collected, validated and turned into something operations can act on.',
        scope:
          'Data collection across field operations — capturing, processing and surfacing resource data for downstream reporting.',
        contribution:
          'Processing services and scheduled jobs moving collected data reliably into operational stores, backed by relational data modelling across the database platforms these programmes run on.',
        concepts: [
          'Background & Scheduled Jobs',
          'Event-Driven Architecture',
          'REST API design',
          'OOP / SOLID',
          'TDD',
        ],
      },
    ],
  },

  approach: {
    eyebrow: 'Engineering approach',
    heading: 'How I work through a real application',
    lede: 'The sequence stays the same whatever the domain. Requirements before code, boundaries before implementation, and nothing shipped that the next engineer cannot reason about.',
    note: 'These patterns come from delivered work — each project uses the subset its problem actually needs, not the whole list.',
    stages: [
      {
        id: 'understand',
        index: '01',
        title: 'Understand the business requirement',
        short: 'Understand',
        intent: 'Start from the operational problem, not the framework.',
        detail:
          'Before any code, establish who uses the system, what decision it supports, and what breaks if it is wrong or late. Most requirements change once they are stated precisely enough to argue with.',
        patterns: [
          'Business requirement analysis',
          'Domain understanding',
          'Acceptance criteria',
        ],
        evidence:
          'Aviation trip and operations planning needed the scheduling rules pinned down before any service was written; the smart home and appliance platform needed device state defined before the workflows around it.',
      },
      {
        id: 'design',
        index: '02',
        title: 'Design the API and application boundaries',
        short: 'Design',
        intent: 'Fix the boundaries before writing the implementation.',
        detail:
          'Decide what each component is responsible for and what the contract between them looks like. Boundaries agreed early are cheap to move; entangled ones are not.',
        patterns: [
          'REST API design',
          'Clean Architecture',
          'Repository Pattern',
          'OOP / SOLID',
        ],
        evidence:
          'Backend services and REST APIs structured with Clean Architecture, CQRS and the Repository pattern, keeping trip and scheduling logic independent of persistence and transport.',
      },
      {
        id: 'build',
        index: '03',
        title: 'Build maintainable backend services',
        short: 'Build',
        intent: 'Keep business rules out of the plumbing.',
        detail:
          'Business logic belongs in the domain, infrastructure sits behind an abstraction, and commands and queries are separated where the complexity earns it. The test is whether a new requirement forces a rewrite or only a new class.',
        patterns: [
          'Clean Architecture',
          'CQRS',
          'Repository Pattern',
          'OOP / SOLID',
        ],
        evidence:
          'In the IT/OT asset and ISMS work, domain and persistence concerns were separated behind a repository abstraction, exposing REST APIs consumed by React front-ends.',
      },
      {
        id: 'integrate',
        index: '04',
        title: 'Integrate external systems and background work',
        short: 'Integrate',
        intent: 'Assume the outside world is slow, then design for it.',
        detail:
          'External systems and long-running work do not belong in a request thread. Isolate them behind messages and queues so a failing dependency degrades instead of cascading, and so slow work never blocks someone waiting on a response.',
        patterns: [
          'Event-Driven Architecture',
          'Azure Service Bus',
          'Outbox messaging',
          'Background & scheduled jobs',
          'Hangfire',
        ],
        evidence:
          'Event-driven services connected device-facing workflows to application services through Azure Service Bus, with Hangfire carrying scheduled and background processing off the request path.',
      },
      {
        id: 'optimise',
        index: '05',
        title: 'Optimise data access',
        short: 'Optimise',
        intent: 'Treat persistence as a design decision, not an afterthought.',
        detail:
          'The right access pattern depends on the store and the query being asked of it. Model for how the data is actually read, and keep persistence details behind the repository so the domain does not move when the schema does.',
        patterns: [
          'Data access optimisation',
          'Query & index design',
          'PostgreSQL',
          'SQL Server',
          'Oracle',
          'MongoDB',
        ],
        evidence:
          'Relational and document data modelled across PostgreSQL, SQL Server, Oracle and MongoDB, with scheduled processing moving collected field data reliably into operational stores in the energy and resources work.',
      },
      {
        id: 'verify',
        index: '06',
        title: 'Test, review and deliver',
        short: 'Verify & ship',
        intent: 'Prove it, then hand over something the team can build on.',
        detail:
          'Test the behaviour that actually matters, review before it lands, and deliver against a contract the rest of the team — including the frontend consuming the API — can work from immediately.',
        patterns: [
          'TDD',
          'Code reviews',
          'Agile / Scrum',
          'Production-oriented delivery',
        ],
        evidence:
          'Worked in an Agile/Scrum delivery cycle with code review practice, agreeing implementation approach alongside frontend developers rather than handing over an API and walking away.',
      },
    ],
  },

  capabilities: {
    eyebrow: 'Technical capabilities',
    heading: 'The layers I work across',
    lede: 'Backend is where the majority of my experience sits. The other groups are the layers I reach into when a problem runs past the service boundary — listed by the part each one plays in a system.',
    additionalLabel: 'Also works with',
    note: 'Items above the divider are evidenced by the work described earlier on this page. Items below it are areas I can work in, listed for coverage rather than as a claim about depth.',
    groups: [
      {
        id: 'backend',
        index: '01',
        title: 'Backend Engineering',
        role: 'Where business capability is exposed as services and APIs — the core of the work.',
        icon: 'backend',
        items: [
          { label: 'C#', documented: true },
          { label: '.NET', documented: true },
          { label: '.NET Core', documented: true },
          { label: 'ASP.NET Core', documented: true },
          { label: 'REST APIs', documented: true },
          { label: 'Entity Framework', documented: false },
          { label: 'Dapper', documented: false },
          { label: 'Java Spring Boot', documented: false },
        ],
      },
      {
        id: 'architecture',
        index: '02',
        title: 'Architecture & Engineering',
        role: 'How the code is arranged so it can absorb change without a rewrite.',
        icon: 'architecture',
        items: [
          { label: 'Clean Architecture', documented: true },
          { label: 'CQRS', documented: true },
          { label: 'Repository Pattern', documented: true },
          { label: 'OOP / SOLID', documented: true },
          { label: 'Event-Driven Architecture', documented: true },
          { label: 'TDD', documented: true },
          { label: 'Code Reviews', documented: true },
        ],
      },
      {
        id: 'data',
        index: '03',
        title: 'Data',
        role: 'Persistence and data access across relational and document stores.',
        icon: 'data',
        items: [
          { label: 'PostgreSQL', documented: true },
          { label: 'SQL Server', documented: true },
          { label: 'Oracle', documented: true },
          { label: 'MongoDB', documented: true },
          { label: 'MySQL', documented: false },
        ],
      },
      {
        id: 'messaging',
        index: '04',
        title: 'Messaging & Processing',
        role: 'Work that belongs outside the request thread, and messages between systems.',
        icon: 'messaging',
        items: [
          { label: 'Azure Service Bus', documented: true },
          { label: 'Hangfire', documented: true },
          { label: 'Background Jobs', documented: true },
          { label: 'Outbox Messaging Pattern', documented: true },
        ],
      },
      {
        id: 'frontend',
        index: '05',
        title: 'Frontend',
        role: 'Enough front-end to build against the API contract and integrate alongside it.',
        icon: 'frontend',
        items: [
          { label: 'React', documented: true },
          { label: 'React TypeScript', documented: true },
          { label: 'JavaScript', documented: false },
          { label: 'HTML', documented: false },
          { label: 'CSS', documented: false },
        ],
      },
      {
        id: 'cloud',
        index: '06',
        title: 'Cloud / Infrastructure',
        role: 'Where the services are hosted and how they are configured to run.',
        icon: 'cloud',
        items: [
          { label: 'Azure', documented: true },
          { label: 'GCP', documented: false },
          { label: 'AWS', documented: false },
          { label: 'Windows Server', documented: false },
        ],
      },
    ],
  },

  timeline: {
    eyebrow: 'Professional journey',
    heading: 'One role, four client engagements',
    lede: 'I have been at Centroxy Solution Pvt. Ltd. since March 2021. Within that one position the work has spanned four client engagements — aviation operations, connected devices, asset and ISMS systems, and energy data.',
    note: 'The resume dates the position, not the individual engagements, so these are presented as workstreams running in parallel within one continuous role rather than as a dated sequence. The delivery was team-based; the contributions listed are my own.',
    position: {
      company: 'Centroxy Solution Pvt. Ltd.',
      role: 'Backend Engineer — Enterprise Application Development',
      period: 'March 2021 – Present',
      duration: '4.5+ years',
    },
    items: [
      {
        id: 'iot-tata',
        ref: '01',
        domain: 'Smart IoT Home & Appliance Platform',
        client: 'Tata Power',
        purpose:
          'A connected home and appliance platform where device state has to stay consistent between the device, the platform, and the person using it.',
        contribution:
          'Backend services across the device and application layers, with the messaging between them handled asynchronously instead of inside the request path.',
        responsibilities: [
          'Backend service development across device and application layers',
          'Event-driven integration between device and application workflows',
          'Background and scheduled processing with Hangfire',
        ],
        technologies: [
          'C# / .NET',
          'REST APIs',
          'Azure Service Bus',
          'Hangfire',
          'Background Jobs',
          'Outbox Messaging',
          'Clean Architecture',
        ],
        detail: [
          'Device onboarding and device state management',
          'Messaging between device-facing workflows and application services',
          'Moving long-running and scheduled work off the request path',
        ],
      },
      {
        id: 'asset-deloitte',
        ref: '02',
        domain: 'IT/OT Asset Management & ISMS Platform',
        client: 'Deloitte India',
        purpose:
          'Asset lifecycle tracking and information security management for organisations running both IT and OT estates, where asset records and compliance evidence have to stay consistent with each other.',
        contribution:
          'Domain and persistence concerns separated behind a repository abstraction, exposing REST APIs that the React front-end consumed.',
        responsibilities: [
          'Backend service and REST API development',
          'Repository abstraction between domain and persistence',
          'Agreeing implementation approach alongside frontend developers',
        ],
        technologies: [
          'C# / .NET',
          'REST APIs',
          'Clean Architecture',
          'Repository Pattern',
          'CQRS',
          'OOP / SOLID',
          'React / TypeScript',
          'TDD',
        ],
        detail: [
          'Asset lifecycle records and ISMS compliance tracking',
          'Reporting that supported audit activity',
          'Working through implementation detail with the frontend developers rather than handing over an API',
        ],
      },
      {
        id: 'energy-smrd',
        ref: '03',
        domain: 'Energy & Resources Data Collection Platform',
        client: 'SMRD Tata Power',
        purpose:
          'Collection of field data across energy and resources operations, processed into something operations could act on and report against.',
        contribution:
          'Processing services and scheduled jobs that moved collected field data reliably into operational stores.',
        responsibilities: [
          'Processing services for collected field data',
          'Scheduled and background job implementation',
          'Relational data modelling for operational reporting',
        ],
        technologies: [
          'C# / .NET',
          'REST APIs',
          'Background Jobs',
          'PostgreSQL',
          'SQL Server',
          'Oracle',
          'MongoDB',
          'OOP / SOLID',
        ],
        detail: [
          'Capturing and validating data originating in the field',
          'Scheduled processing moving collected data into operational stores',
          'Data models supporting downstream operational reporting',
        ],
      },
      {
        id: 'aviation-uwa',
        ref: '04',
        domain: 'Aviation Trip & Operations Management Platform',
        client: 'Universal Weather and Aviation',
        purpose:
          'Trip and operations management for an aviation operator — planning, tracking and reporting across trips and the operational activity around them.',
        contribution:
          'Backend services and REST APIs structured with Clean Architecture, CQRS and the Repository pattern, keeping trip and scheduling rules independent of persistence and transport.',
        responsibilities: [
          'Backend service and REST API development',
          'Clean Architecture separation of domain, application and infrastructure',
          'CQRS and repository-based persistence access',
        ],
        technologies: [
          'C# / .NET',
          'REST APIs',
          'Clean Architecture',
          'CQRS',
          'Repository Pattern',
          'OOP / SOLID',
        ],
        detail: [
          'Trip and scheduling logic held independent of persistence and transport',
          'Command and query separation where the domain complexity required it',
          'Repository abstraction over the data access layer',
        ],
      },
    ],
  },

  clientCta: {
    eyebrow: 'Freelance & project work',
    heading: 'Have a system you need to build or improve?',
    lede: 'I build backend services and APIs against real business requirements, and work on the applications already in place. Describe the process you need to support and I will tell you what it would take.',
    capabilities: [
      {
        label: 'Backend & API development',
        detail: 'REST services, domain models and background processing in C# / .NET.',
      },
      {
        label: 'Full-stack application development',
        detail: 'Backend services built alongside the front-end that consumes them.',
      },
      {
        label: 'System integrations',
        detail: 'Event-driven workflows and messaging between systems that were not designed to talk.',
      },
      {
        label: 'Database-backed applications',
        detail: 'Relational and document data modelling, with data access that holds up under load.',
      },
      {
        label: 'Existing application improvements',
        detail: 'Restructuring, refactoring and performance work on code that is already in production.',
      },
      {
        label: 'Business workflow implementation',
        detail: 'Turning an operational process into software people actually use.',
      },
    ],
    cta: {
      primary: { label: 'Start a conversation', to: '/get-in-touch' },
      secondary: { label: 'View projects', to: '/projects' },
    },
  },
};
